import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { useIntl, defineMessages } from 'react-intl'
import { Formik, Form } from 'formik'
import merge from 'lodash/merge'
import objectHash from 'object-hash'
import { useDispatch } from 'react-redux'
import { styled } from '@mui/material/styles'
import Grid from '@mui/material/Grid'
import MikeButton from '@mike/mike-shared-frontend/mike-button'
import messages from '../../shared/messages'
import { ACCESS_LEVELS } from '../../helpers/project'
import TextField from '../formik/TextField'
import ProjectMembersField from '../formik/ProjectMembersField'
import { useTypedSelector } from '../../reducers'
import { loadCustomerUsers } from '../../actions/customerUsers'

const defaultInitialValues = {
  name: '',
  description: '',
  accessLevel: ACCESS_LEVELS.SHARED,
  members: []
}

const validateForm = values => {
  const errors: { name?: string } = {}

  if (!values.name) {
    errors.name = 'Required'
  }

  return errors
}

const localMessages = defineMessages({
  membersPlaceholder: {
    id: 'createProjectForm.members.placeholder'
  },
  createProjectButton: {
    id: 'createProjectForm.createProjectButton'
  },
  editProjectButton: {
    id: 'createProjectForm.editProjectButton'
  },
  createSubprojectButton: {
    id: 'createProjectForm.createSubprojectButton'
  },
  editSubprojectButton: {
    id: 'createProjectForm.editSubprojectButton'
  }
})

const StyledSubmitButton = styled(MikeButton)(({ theme }) => ({
  float: 'right'
}))

const CreateProjectForm = props => {
  const {
    editing = false,
    hideMemberField = false,
    initialValues,
    loadingProjectMembers = false,
    onSubmit,
    isSubProject = false,
    isFolder = false
  } = props

  const intl = useIntl()
  const customerUsers = useTypedSelector(state => state['customerUsers'].data)
  const tenantId = useTypedSelector(state => state['auth'].user?.tenantId)

  const mergedInitialValues = editing
    ? {
        ...merge({}, defaultInitialValues, initialValues),
        members: initialValues.members ?? []
      }
    : {
        ...defaultInitialValues,
        members: []
      }

  const dispatch = useDispatch()

  useEffect(() => {
    if (!customerUsers || Object.keys(customerUsers).length === 0) {
      dispatch(loadCustomerUsers(tenantId))
    }
  }, [])

  const { canEdit = true, canGrantAccess = true } = editing
    ? initialValues.capabilities
    : {}

  // When the member field is hidden (editing), membership is managed by the
  // dedicated access panel. Drop the stale `members` snapshot from the payload
  // so saving other fields doesn't overwrite membership.
  const handleFormSubmit = (values, helpers) => {
    if (hideMemberField) {
      const { members, ...rest } = values
      onSubmit(rest, helpers)
    } else {
      onSubmit(values, helpers)
    }
  }

  return (
    <Formik
      key={objectHash(mergedInitialValues)}
      initialValues={mergedInitialValues}
      validate={validateForm}
      onSubmit={handleFormSubmit}
    >
      {formik => {
        const { handleSubmit, handleChange, setFieldTouched } = formik

        const change = (name, e) => {
          handleChange(e)
          setFieldTouched(name, true, false)
        }

        return (
          <Form>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  name="name"
                  label={intl.formatMessage(messages.name)}
                  disabled={!canEdit}
                  fullWidth
                  required
                  autoFocus
                  onChange={change.bind(null, 'name')}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  name="description"
                  label={intl.formatMessage(messages.description)}
                  disabled={!canEdit}
                  fullWidth
                  type="textarea"
                  rows={4}
                  onChange={change.bind(null, 'description')}
                />
              </Grid>

              {!hideMemberField && (
                <Grid item xs={12}>
                  <ProjectMembersField
                    name="members"
                    disabled={!canGrantAccess}
                    loading={loadingProjectMembers}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <StyledSubmitButton
                  color="secondary"
                  variant="contained"
                  onClick={handleSubmit as any}
                  type="button"
                >
                  {editing
                    ? isSubProject
                      ? intl.formatMessage(localMessages.editSubprojectButton)
                      : intl.formatMessage(localMessages.editProjectButton)
                    : isSubProject
                    ? intl.formatMessage(localMessages.createSubprojectButton)
                    : intl.formatMessage(
                        isFolder
                          ? localMessages.createSubprojectButton
                          : localMessages.createProjectButton
                      )}
                </StyledSubmitButton>
              </Grid>
            </Grid>
          </Form>
        )
      }}
    </Formik>
  )
}
CreateProjectForm.propTypes = {
  editing: PropTypes.bool,
  hideMemberField: PropTypes.bool,
  initialValues: PropTypes.object,
  loadingCustomerUsers: PropTypes.bool,
  loadingProjectMembers: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  isSubProject: PropTypes.bool,
  isFolder: PropTypes.bool,
  totalCount: PropTypes.number
}

export default CreateProjectForm
